import React from "react";

import {
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Badge,
  Button,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  useNumberInput,
} from "@chakra-ui/react";
import { useController } from "react-hook-form";

import { useAgencyValue } from "#app/hooks/index.js";
import {
  getCapacityUnit,
  capacityUnitLabel,
} from "#app/utils/availabilityRequest.js";
import { DEFAULT_MAX_PARTY_SIZE } from "#app/config/partySize.js";

export function MinCapacityInput({
  facility,
  name,
  formContext,
  max = DEFAULT_MAX_PARTY_SIZE,
  ...rest
}) {
  const {
    control,
    formState: { errors },
  } = formContext;

  const {
    field: { value, onChange, ref },
  } = useController({
    name,
    control,
    // Explicit defaultValue guards against RHF handing back `undefined`,
    // which would flip Chakra's useNumberInput into uncontrolled mode and
    // silently drop the field from the submitted form data.
    defaultValue: 1,
    rules: {
      required: { value: true, message: "Please input a number" },
      min: { value: 1, message: `Must select at least one` },
      max: { value: max, message: `Can't select more than ${max}` },
    },
  });

  const unit = getCapacityUnit(facility);
  const label = capacityUnitLabel(unit, value);

  // Reset out of bounds value if new date range is selected
  // after the number of nights
  React.useEffect(() => {
    if (value > max) {
      onChange(max);
    }
  }, [value, max, onChange]);

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      // `name` is forwarded to the rendered <input> so the field survives
      // a native form submission if JS fails to intercept (in-app webview quirks).
      name,
      value,
      onChange: (v) => {
        onChange(v ? parseInt(v) : 0);
      },
      min: 1,
      max,
    });

  // A tent-pad request never asks for a headcount, so say where it goes.
  const tentPadHelperText =
    unit === "tent_pads"
      ? "Count tent pads, not people. You'll enter your party size at checkout."
      : null;

  const agencyHelperText = useAgencyValue({
    base: null,
    NingalooCoralBay:
      "For shared rooms, we'll look for one available bed per person in the same room type. Private rooms are booked as a whole room.",
    ParksCanada: tentPadHelperText,
    BCParks: tentPadHelperText,
  });

  const helperText = agencyHelperText;

  return (
    <FormControl isInvalid={errors[name]} isRequired {...rest}>
      <FormLabel>
        {unit === "tent_pads"
          ? "How many tent pads do you need?"
          : unit === "seats"
            ? "How many seats do you need?"
            : unit === "sites"
              ? "How many sites do you need?"
              : unit === "slots"
                ? "How many slots do you need?"
                : "How many people in your party?"}
      </FormLabel>
      <HStack maxW="320px">
        <Button {...getDecrementButtonProps()}>-</Button>
        <InputGroup maxW="125px">
          <Input {...getInputProps()} ref={ref} />
          <InputRightAddon
            children={label}
            paddingX={2}
            bg="inherit"
            borderLeftWidth="0px"
          />
        </InputGroup>
        <Button {...getIncrementButtonProps()}>+</Button>
      </HStack>
      {errors[name] ? (
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      ) : (
        <FormHelperText>
          {/* At the cap the "+" button silently stops working — say why. */}
          {max > 1 && value >= max && (
            <Text color="warning" fontWeight="semibold">
              {max} {capacityUnitLabel(unit, max)} is the most this park
              supports.
            </Text>
          )}
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
